import axios, { AxiosError, AxiosResponse, AxiosInstance, AxiosRequestConfig } from 'axios';
import { logger } from 'utils';

export const logError: (error?: AxiosError) => void = (error = undefined) => {
  if (process.env.NODE_ENV === 'production') {
    return;
  }
  if (!error) {
    return undefined;
  }
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    logger.error('An error occurred processing the remote reques', {
      data: error.response.data,
      status: error.response.status,
      headers: error.response.headers
    });
  } else if (error.request) {
    // The request was made but no response was received
    // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
    // http.ClientRequest in node.js
    logger.error('An error occurred sending the request and no response was received', {
      request: error.request
    });
  } else {
    // Something happened in setting up the request that triggered an Error
    logger.error('An unexpected error occurred', {
      error: error.message
    });
  }
};

/**
 * Type-guard function to determine of given object isntanceOf AxiosError
 * @param err
 */
export function isError(err): err is AxiosError {
  return err &&
    err.response &&
    err.response.data &&
    err.response.status &&
    err.response.headers &&
    err.request
    ? true
    : false;
}

export class SVError extends Error {
  constructor(message?: string) {
    super(message);
    this.name = 'SVError';
  }
}

export class Adapter {
  /**
   * Reference to an instance of the api
   */
  private instance: AxiosInstance;

  constructor(config: AxiosRequestConfig) {
    // Use these instances of axios to make network requests to the testservice API's
    this.instance = axios.create(config);
  }

  updateConfig = (config: AxiosRequestConfig) => {
    this.instance.defaults = config;
  };

  async getValue<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    try {
      return this.instance.get(url, config);
    } catch (error) {
      if (isError(error)) {
        logError(error);
      }
      return Promise.reject(new SVError(JSON.stringify(error)));
    }
  }

  async postValue<T>(
    url: string,
    body: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    try {
      return this.instance.post(url, body, config);
    } catch (error) {
      if (isError(error)) {
        logError(error);
      }
      return Promise.reject(new SVError(JSON.stringify(error)));
    }
  }

  async putValue<T>(
    url: string,
    body: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    try {
      return this.instance.put(url, body, config);
    } catch (error) {
      if (isError(error)) {
        logError(error);
      }
      return Promise.reject(new SVError(JSON.stringify(error)));
    }
  }

  async deleteValue<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    try {
      return this.instance.delete(url, config);
    } catch (error) {
      if (isError(error)) {
        logError(error);
      }
      return Promise.reject(new SVError(JSON.stringify(error)));
    }
  }
}
